/**
 * Event-based resource loader: one `createResourceProcessor` per resource type, so
 * a new type registers a processor, not new methods here. A caller that knows the
 * type uses a named accessor (`loader.textures`), a data-driven one `request(type, path)`.
 */
import { ResourceEventBus } from './ResourceEventBus';
import { MetadataStore } from './MetadataStore';
import { createTextureProcessor } from './processors/createTextureProcessor';
import { createTexture3DProcessor } from './processors/createTexture3DProcessor';
import { createTexture2DArrayProcessor } from './processors/createTexture2DArrayProcessor';
import { createGLBProcessor } from './processors/createGLBProcessor';
import { createSceneProcessor } from './processors/createSceneProcessor';
import { createTresResourceProcessor } from './processors/createTresResourceProcessor';
import { createArrayMeshProcessor } from './processors/createArrayMeshProcessor';
import { createFontProcessor } from './processors/createFontProcessor';
import { createThemeProcessor } from './processors/createThemeProcessor';
import { runClearCachesSequence } from './clearCachesSequence';
import { runProvideFileSequence } from './provideFileSequence';
import './sliceRegistrations.js';
import { PEER_LOAD_TIMEOUT_MS } from './createResourceProcessor';
import { DependencyGraph } from './dependencyGraph';
import { sectionLoader } from './resourceSection';
import { resourceFilePath } from './subResourcePath';
import * as logger from '../logger';
import { WorkerJobRunner } from '../workers/WorkerJobRunner';
export class ResourceLoader {
    metadata;
    /** Which cached resources read which files, so a **Dependency hot-reload** reaches them. */
    dependencies = new DependencyGraph();
    /**
     * File path → the `busType:address` builds still reading its parse. Written only by
     * `holdParse` and `settleHold`. Cleared by `clear`, which drops the settle listener.
     */
    parseHolds = new Map();
    /** The bus types whose builds read a section. Written only by `sectionLoaderFor`. */
    sectionBusTypes = [];
    /** The one settle listener for every hold, subscribed by the first hold after a `clear`. */
    unsubscribeHolds = null;
    /** Runs procedural texture builds, in the host's worker where it has one. */
    jobRunner;
    eventBus;
    textures;
    glbMeshes;
    scenes;
    /** Each `.tres` parsed once: the parse every slice that decodes Godot text reads. */
    resources;
    /** ArrayMesh .tres decoded into geometry + per-surface material paths. */
    arrayMeshes;
    /** FontFile/SystemFont/FontVariation, recursively resolved (base_font, fallbacks). */
    fonts;
    /** Theme .tres, font-relevant fields resolved (default_font, <Type>/fonts/<name>); everything else raw. */
    themes;
    /** Type to processor. The named accessors above are entries of this map, so routing needs no per-type switch. */
    processors;
    provider = null;
    _fileEventBus;
    /**
     * How many mounted `useResource` consumers are still waiting: the signal that
     * loading has finished, which a timer can only guess at. It lives here, not in a
     * context of its own, so no host mounts another provider.
     */
    pendingResources = 0;
    pendingListeners = new Set();
    /** Called by `useResource` while a consumer is pending; returns the release. */
    beginPending() {
        this.pendingResources += 1;
        this.notifyPending();
        let released = false;
        return () => {
            if (released)
                return;
            released = true;
            this.pendingResources -= 1;
            this.notifyPending();
        };
    }
    get pendingResourceCount() {
        return this.pendingResources;
    }
    /** Subscribe to pending-count changes; returns the unsubscribe. */
    subscribePending(listener) {
        this.pendingListeners.add(listener);
        return () => this.pendingListeners.delete(listener);
    }
    notifyPending() {
        for (const listener of this.pendingListeners)
            listener();
    }
    /**
     * Await a peer processor's resource for `dependent`: answer from cache, else request
     * it and wait on that processor's bus slot. Rejects with the peer's failure. Bounded,
     * far above any real fetch: an unbounded await would leave a GLB whose peer never settles
     * neither appearing nor reporting missing.
     */
    async peerRequire(dependent, processor, busType, path) {
        // First, so a change to `path` reloads `dependent` whether this read hits, fails or waits.
        this.dependencies.record(dependent, path);
        const cached = processor.getCached(path);
        if (cached === null)
            throw processor.failure(path);
        if (cached !== undefined)
            return cached;
        processor.request(path);
        return this.eventBus.once(busType, 'loaded', path, PEER_LOAD_TIMEOUT_MS);
    }
    /** {@link peerRequire}, with null for a peer that failed or never answered. */
    async peerLoad(dependent, processor, busType, path) {
        try {
            return await this.peerRequire(dependent, processor, busType, path);
        }
        catch {
            return null;
        }
    }
    /**
     * A file's bytes for a processor that routes by path before it fetches, read from the
     * byte layer's provider. Not `tryLoad`: it answers every failure as absence and drops
     * the provider's reason, and clearing its cached copy would drop a peer's flight too.
     */
    async readFile(path, type) {
        const fileBus = this._fileEventBus;
        if (!fileBus)
            throw new Error(`No file bus to read ${path}`);
        const data = await fileBus.getProvider().loadResource(path, type);
        if (data === null)
            throw new Error(`File not found: ${path}`);
        return data;
    }
    /** The section behind each address `busType` loads, from the `resource` slot's cached parse. */
    sectionLoaderFor(busType) {
        this.sectionBusTypes.push(busType);
        return sectionLoader((path) => {
            this.holdParse(busType, path);
            return this.peerRequire({ busType, key: path }, this.resources, 'resource', resourceFilePath(path));
        });
    }
    /**
     * Hold the parse of `address`'s file until `address` settles, then release it with the
     * last holder. A built resource then keeps no copy of its file's text beside it, and
     * overlapping builds, such as a MeshLibrary's meshes or a Theme's fonts, share one parse.
     */
    holdParse(busType, address) {
        const filePath = resourceFilePath(address);
        const holders = this.parseHolds.get(filePath) ?? new Set();
        this.parseHolds.set(filePath, holders.add(`${busType}:${address}`));
        // One listener, not one per build: each emit would otherwise call every pending build's.
        // Synchronous, not `once`: a reader woken by the settle event already sees the parse gone.
        this.unsubscribeHolds ??= this.eventBus.onChange(this.sectionBusTypes, (change) => this.settleHold(change));
    }
    settleHold({ busType, key }) {
        const filePath = resourceFilePath(key);
        const holders = this.parseHolds.get(filePath);
        // Still loading after the event: a consumer re-requested on `invalidated`, so the build goes on.
        if (!holders || this.processors.get(busType)?.isLoading(key))
            return;
        holders.delete(`${busType}:${key}`);
        if (holders.size > 0)
            return;
        this.parseHolds.delete(filePath);
        this.resources.release(filePath);
    }
    constructor(fileEventBus, options = {}) {
        this._fileEventBus = fileEventBus || null;
        this.jobRunner = new WorkerJobRunner({ createWorker: options.createWorker });
        this.eventBus = new ResourceEventBus();
        this.metadata = new MetadataStore();
        // An image's **Import sidecar** decides the format its texture holds after import.
        this.textures = createTextureProcessor(fileEventBus, this.eventBus, this.dependencies);
        // A GLB's **Import sidecar** can repoint a glTF material at an external `.tres`. The
        // processor tags the surface, and the scene root draws that `.tres` like any material.
        this.glbMeshes = createGLBProcessor(fileEventBus, this.eventBus, this.dependencies, options.gltfExtensions);
        // PackedScene loads directly (`loadDirectly`). The id-to-path translation reads the
        // shared MetadataStore.
        this.scenes = createSceneProcessor({
            eventBus: this.eventBus,
            resolveMetadata: (idOrPath) => {
                const meta = this.metadata.get(idOrPath);
                if (meta)
                    return { path: meta.path, type: meta.type };
                // A raw `res://` `instance` names no ExtResource, so nothing registers
                // it: the address is its own path, and its type is unknown. Callers request
                // the simplified address (`resolveInstancePath`), the processor's cache key.
                return idOrPath.startsWith('res://') ? { path: idOrPath, type: null } : null;
            },
            getProvider: () => this.provider,
        });
        this.resources = createTresResourceProcessor(fileEventBus, this.eventBus);
        this.arrayMeshes = createArrayMeshProcessor(this.eventBus, this.sectionLoaderFor('arraymesh'));
        this.fonts = createFontProcessor(this.eventBus, this.sectionLoaderFor('font'), (path) => this.readFile(path, 'FontFile'), this.dependencies);
        // A Theme's font refs resolve through the FONT processor (a different peer, unlike a
        // Font's own self-recursion).
        this.themes = createThemeProcessor(this.eventBus, this.sectionLoaderFor('theme'), (themeKey) => (address) => this.peerLoad({ busType: 'theme', key: themeKey }, this.fonts, 'font', address));
        this.processors = new Map([
            ['texture', this.textures],
            [
                'texture3d',
                createTexture3DProcessor(fileEventBus, this.eventBus, this.dependencies),
            ],
            [
                'texture2darray',
                createTexture2DArrayProcessor(fileEventBus, this.eventBus, this.dependencies),
            ],
            ['glb', this.glbMeshes],
            ['scene', this.scenes],
            ['resource', this.resources],
            ['arraymesh', this.arrayMeshes],
            ['font', this.fonts],
            ['theme', this.themes],
        ]);
    }
    /**
     * The byte layer, for a file found by convention rather than declared by a scene
     * (`project.godot`, an `.import` sidecar), read with `tryLoad`, whose miss is an
     * ordinary answer, not a **Missing resource**. What a scene names goes through a processor.
     */
    get fileEventBus() {
        return this._fileEventBus;
    }
    /** Register an external resource from parsed TSCN data. */
    register(resource) {
        this.metadata.register(resource);
    }
    setProvider(provider) {
        this.provider = provider;
    }
    /** The processor serving `type`, for a consumer that reads, requests and pins by path. */
    processor(type) {
        return this.processors.get(type);
    }
    /** Request a resource through the processor for `type`. */
    request(type, path) {
        const proc = this.processors.get(type);
        if (!proc) {
            logger.warn(`[ResourceLoader] Unknown resource type: ${type}`);
            return;
        }
        proc.request(path);
    }
    /** Read a cached resource. Returns undefined when never requested. */
    getCached(type, path) {
        const proc = this.processors.get(type);
        if (!proc)
            return undefined;
        return proc.getCached(path);
    }
    /**
     * Clear all caches, metadata and subscribers, and announce no `invalidated`,
     * since no subscriber is left to hear it.
     */
    clear() {
        this.metadata.clear();
        this.dependencies.clear();
        this.parseHolds.clear();
        this.unsubscribeHolds = null;
        for (const proc of this.processors.values()) {
            proc.clearCache();
        }
        this.eventBus.clear();
        logger.info('[ResourceLoader] Cleared all caches');
    }
    /**
     * Drop every cached resource, file byte and metadata entry, but keep subscribers,
     * for a switch to a corpus whose res:// paths would alias the old one's cache.
     * The host repoints the provider first and calls this only after the old scene is
     * torn down: announced consumers refetch at once under the current provider.
     */
    clearCaches() {
        this.dependencies.clear();
        runClearCachesSequence({
            processors: this.processors,
            eventBus: this.eventBus,
            metadata: this.metadata,
            clearFileBus: () => this._fileEventBus?.clearCache(),
            log: () => logger.info('[ResourceLoader] Cleared caches (subscribers kept)'),
        });
    }
    /**
     * A file changed, appeared or went away (a **Dependency hot-reload**, a **Resource
     * upload** or its removal): drop and announce every resource built from it or that
     * read it. A mounted consumer requests each again. An address names its whole file.
     */
    provideFile(path) {
        logger.info(`[ResourceLoader] provideFile: ${path}`);
        runProvideFileSequence({
            path,
            processors: this.processors,
            fileBus: this._fileEventBus ?? undefined,
            dependencies: this.dependencies,
        });
    }
}
//# sourceMappingURL=ResourceLoader.js.map